import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Loader2, Search, UserCircle2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { SearchUsersParams } from '@/utils/api/user-service/models'

import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser, isOrganisationAdminUser } from '@/lib/role-guards'
import { cn } from '@/lib/utils'
import { getUserDetailsById, useSearchUsers } from '@/utils/api/user-service'

interface UserOption {
  id: number
  email: string
  name: string
}

interface UserSearchInputProps {
  value?: number
  onValueChange: (userId: number | undefined) => void
  prefilledUser?: {
    id: number
    name?: string
    email?: string
  }
  label?: string
  placeholder?: string
  id?: string
  required?: boolean
  disabled?: boolean
  disableOrganisationFilter?: boolean
  className?: string
}

export function UserSearchInput({
  value,
  onValueChange,
  prefilledUser,
  label = 'User',
  placeholder = 'Search user by email...',
  id,
  required = false,
  disabled = false,
  disableOrganisationFilter = false,
  className,
}: UserSearchInputProps) {
  const { user: currentUser } = useAuth()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Determine if user is admin or org admin
  const isAdmin = isAdminUser(currentUser)
  const isOrgAdmin = isOrganisationAdminUser(currentUser)

  // Build search params based on role
  const searchParams = useMemo<SearchUsersParams>(() => {
    const params: SearchUsersParams = {
      q: debouncedQuery || '',
      page: 0,
      size: 20,
      status: 'ACTIVE',
    }
    return params
  }, [debouncedQuery])

  // Only fetch if we have a search query
  const searchQuery_enabled = debouncedQuery.trim().length >= 2

  const usersQuery = useSearchUsers(searchParams, {
    query: {
      enabled: searchQuery_enabled,
      staleTime: 30_000,
    },
  })

  // Transform API results to options
  const users = useMemo(() => {
    const content = usersQuery.data?.data?.content ?? []

    // Filter by organization if user is org admin (not system admin)
    const filtered = isOrgAdmin && !isAdmin && !disableOrganisationFilter
      ? content.filter(u => u.mainOrgainisationId === currentUser?.mainOrgainisationId)
      : content

    return filtered
      .filter((u): u is typeof u & { id: number } => typeof u.id === 'number' && u.id > 0)
      .map((u): UserOption => ({
        id: u.id,
        email: typeof u.email === 'string' ? u.email : '',
        name: typeof u.name === 'string' ? u.name : (typeof u.email === 'string' ? u.email : ''),
      }))
  }, [usersQuery.data?.data?.content, isAdmin, isOrgAdmin, currentUser?.mainOrgainisationId, disableOrganisationFilter])

  const hasSelectedValue = typeof value === 'number' && value > 0
  const hasSelectedInUsers = useMemo(() => users.some(user => user.id === value), [users, value])
  const selectedUserDetailsQuery = useQuery({
    queryKey: ['user-details-by-id', value],
    queryFn: () => getUserDetailsById(value as number),
    enabled: hasSelectedValue && !hasSelectedInUsers,
    staleTime: 5 * 60 * 1000,
  })
  const selectedUserFromApi = useMemo<UserOption | undefined>(() => {
    const user = selectedUserDetailsQuery.data?.data
    if (!user?.id) {
      return undefined
    }
    return {
      id: user.id,
      email: typeof user.email === 'string' ? user.email : '',
      name: typeof user.name === 'string' ? user.name : (typeof user.email === 'string' ? user.email : `User ${user.id}`),
    }
  }, [selectedUserDetailsQuery.data?.data])
  const usersWithSelected = useMemo(() => {
    if (!selectedUserFromApi) {
      return users
    }
    if (users.some(user => user.id === selectedUserFromApi.id)) {
      return users
    }
    return [selectedUserFromApi, ...users]
  }, [selectedUserFromApi, users])

  const selectedUser = useMemo(() => {
    const matchedUser = usersWithSelected.find(u => u.id === value)
    if (matchedUser) {
      return matchedUser
    }
    if (prefilledUser && prefilledUser.id === value) {
      return {
        id: prefilledUser.id,
        email: prefilledUser.email ?? '',
        name: prefilledUser.name ?? prefilledUser.email ?? `User ${prefilledUser.id}`,
      }
    }
    return undefined
  }, [usersWithSelected, value, prefilledUser])

  const handleSelect = useCallback((userId: number) => {
    onValueChange(userId === value ? undefined : userId)
    setOpen(false)
  }, [onValueChange, value])

  const isLoadingUsers = usersQuery.isLoading || selectedUserDetailsQuery.isLoading

  const displayText = selectedUser
    ? (selectedUser.email ? `${selectedUser.name} (${selectedUser.email})` : selectedUser.name)
    : placeholder

  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className={cn('truncate', !selectedUser && 'text-muted-foreground')}>
              {displayText}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
          <Command shouldFilter={false} className="rounded-lg border-0">
            <div className="relative border-b mt-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/60" />
              <CommandInput
                placeholder="Type email or name to search..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                className="pl-2 border-0 mb-4 focus:ring-0"
              />
              {isLoadingUsers && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <CommandList className="max-h-[280px]">
              <CommandEmpty>
                <div className="flex flex-col items-center justify-center gap-2 py-6 text-center">
                  {isLoadingUsers
                    ? (
                      <>
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Searching users...</p>
                      </>
                    )
                    : searchQuery.trim().length < 2
                      ? (
                        <>
                          <Search className="h-8 w-8 text-muted-foreground/50" />
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Start typing to search</p>
                            <p className="text-xs text-muted-foreground/70 mt-1">Enter at least 2 characters</p>
                          </div>
                        </>
                      )
                      : (
                        <>
                          <UserCircle2 className="h-8 w-8 text-muted-foreground/50" />
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">No users found</p>
                            <p className="text-xs text-muted-foreground/70 mt-1">Try a different search term</p>
                          </div>
                        </>
                      )}
                </div>
              </CommandEmpty>
              {usersWithSelected.length > 0 && (
                <CommandGroup heading={`${usersWithSelected.length} user${usersWithSelected.length !== 1 ? 's' : ''} found`}>
                  {usersWithSelected.map(user => (
                    <CommandItem
                      key={user.id}
                      value={String(user.id)}
                      onSelect={() => handleSelect(user.id)}
                      className="flex items-center gap-3 py-3"
                    >
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0',
                          value === user.id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <UserCircle2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                      <div className="flex flex-col overflow-hidden">
                        <span className="truncate font-medium text-sm">{user.name}</span>
                        <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
